import React from "react";
import { useDropzone } from "react-dropzone";

import { Translate } from "@/app/application/Translate";
import { sharedTaskDialogPanelSx } from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";

type TaskAttachmentDropzoneProps = {
	fileName?: string;
	disabled?: boolean;
	onFileSelected: (file: File) => void;
	onClear: () => void;
};

const TaskAttachmentDropzone = ({
	fileName,
	disabled = false,
	onFileSelected,
	onClear,
}: TaskAttachmentDropzoneProps) => {
	const theme = useTheme();
	const dropzone = useDropzone({
		disabled,
		multiple: false,
		onDrop: (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (file) {
				onFileSelected(file);
			}
		},
	});

	return (
		<Box
			{...dropzone.getRootProps()}
			sx={{
				...sharedTaskDialogPanelSx(theme),
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				border: "2px dashed",
				borderColor: dropzone.isDragActive ? "primary.main" : "divider",
				py: 3,
				px: 2,
				textAlign: "center",
				cursor: disabled ? "default" : "pointer",
				bgcolor: dropzone.isDragActive ? "action.hover" : "background.default",
				color: "text.secondary",
				transition: "border-color 0.2s, background-color 0.2s",
				"&:hover": disabled
					? undefined
					: {
							borderColor: "primary.main",
							bgcolor: "action.hover",
						},
				opacity: disabled ? 0.7 : 1,
			}}
		>
			<input
				key={fileName ?? "empty"}
				{...dropzone.getInputProps()}
				id="task-attachment-file-input"
			/>
			{fileName ? (
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					sx={{
						p: 2,
						borderRadius: 1,
						bgcolor: "background.paper",
						width: "100%",
						maxWidth: 420,
						border: "1px solid",
						borderColor: "divider",
					}}
				>
					<InsertDriveFileOutlinedIcon
						sx={{ color: "text.secondary", fontSize: 32 }}
					/>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body2" fontWeight={600} noWrap>
							{fileName}
						</Typography>
					</Box>
					<IconButton
						onClick={(event) => {
							event.stopPropagation();
							onClear();
						}}
						color="error"
						size="small"
						aria-label="Remove file"
						disabled={disabled}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
			) : (
				<>
					<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
						<Translate String="TaskAttachmentDropzone.DragAFileHereOrClickToBrowse">
							Drag a file here or click to browse
						</Translate>
					</Typography>
					<Typography variant="caption">
						<Translate String="TaskAttachmentDropzone.SelectOneAttachment">
							Select one attachment
						</Translate>
					</Typography>
				</>
			)}
		</Box>
	);
};

export default TaskAttachmentDropzone;
